import React, { useState, useRef } from 'react';
import { Card, CardContent } from './card';
import { Button } from './button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './dialog';
import { cn } from '../../lib/utils';
import { Upload, Check, X, Loader2 } from 'lucide-react';
import { useAuth } from '../../auth';
import { useOps } from '../../hooks/useOps';
import { config } from '../../utils/config';
import { useFileUpload } from '../../features/upload/hooks/useFileUpload';

// File type whitelist
const ALLOWED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'text/plain': ['.txt'],
  'application/json': ['.json'],
  'text/xml': ['.xml'],
  'application/xml': ['.xml']
};

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

interface ValidationResult {
  isValid: boolean;
  error?: string;
}

interface UploadBoxProps {
  title: string;
  docType: string;
  allowedExtensions: string;
  icon?: React.ReactNode;
  span?: number;
  className?: string;
  /** Banco de destino. Sem isto, cai no banco do stage atual. */
  dbName?: string;
  onUploadComplete?: () => void;
  onUploadStart?: () => void;
}

const validateFile = (file: File, allowedTypes: string): ValidationResult => {
  // Validate file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `Arquivo muito grande. Tamanho máximo: ${MAX_FILE_SIZE / (1024 * 1024)}MB`
    };
  }

  // Create filtered whitelist based on allowed types
  const filteredWhitelist: Record<string, string[]> = {};
  
  if (typeof allowedTypes === 'string') {
    const allowedExtensions = allowedTypes.toLowerCase().split(',').map(ext => ext.trim());

    Object.entries(ALLOWED_FILE_TYPES).forEach(([mimeType, extensions]) => {
      const hasMatchingExtension = extensions.some(ext =>
        allowedExtensions.includes(ext) || allowedExtensions.includes(ext.substring(1))
      );
      if (hasMatchingExtension) {
        const filteredExtensions = extensions.filter(ext =>
          allowedExtensions.includes(ext) || allowedExtensions.includes(ext.substring(1))
        );
        if (filteredExtensions.length > 0) {
          filteredWhitelist[mimeType] = filteredExtensions;
        }
      }
    });
  } else {
    Object.assign(filteredWhitelist, ALLOWED_FILE_TYPES);
  }

  // Validate MIME type against filtered whitelist
  const allowedExtensionsForType = filteredWhitelist[file.type];
  if (!allowedExtensionsForType) {
    return {
      isValid: false,
      error: `Tipo de arquivo não permitido: ${file.type}. Tipos permitidos: ${Object.keys(filteredWhitelist).join(', ')}`
    };
  }

  // Validate file extension
  const fileName = file.name.toLowerCase();
  const hasValidExtension = allowedExtensionsForType.some(ext => fileName.endsWith(ext));

  if (!hasValidExtension) {
    return {
      isValid: false,
      error: `Extensão de arquivo não permitida. Extensões válidas: ${allowedExtensionsForType.join(', ')}`
    };
  }

  return { isValid: true };
};

export function UploadBox({
  title,
  docType,
  allowedExtensions,
  icon,
  span = 1,
  className,
  dbName: dbNameProp,
  onUploadComplete,
  onUploadStart
}: UploadBoxProps) {
  const [success, setSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [failedFiles, setFailedFiles] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { getCurrentOpsConfig } = useOps();
  const uploadMutation = useFileUpload();

  const handleClose = () => {
    setShowErrorDialog(false);
  };

  const handleButtonClick = () => {
    setUploadError(false);
    setSuccess(false);
    setFailedFiles([]);
    setValidationErrors([]);
    fileInputRef.current?.click();
  };

  const fileUploadHandler = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setValidationErrors([]);
    onUploadStart?.();

    const validationErrors: string[] = [];
    const validFiles: File[] = [];

    files.forEach(file => {
      const validation = validateFile(file, allowedExtensions);
      if (!validation.isValid) {
        validationErrors.push(`${file.name}: ${validation.error}`);
      } else {
        validFiles.push(file);
      }
    });

    if (validationErrors.length > 0) {
      setValidationErrors(validationErrors);
      setUploadError(true);
      setShowErrorDialog(true);
      return;
    }

    // opsConfigs nunca chega a receber dbName (nenhum lugar despacha addOpsConfig/
    // updateOpsConfig), então sem o fallback do stage todo upload morria aqui em
    // "Banco de dados não configurado" sem sequer chamar a API.
    const opsConfig = getCurrentOpsConfig();
    const dbName = dbNameProp || opsConfig?.dbName || `xama_${config.stage}`;

    if (!user?.email) {
      setUploadError(true);
      setFailedFiles(['Usuário não autenticado']);
      setShowErrorDialog(true);
      return;
    }

    if (!dbName) {
      setUploadError(true);
      setFailedFiles(['Banco de dados não configurado']);
      setShowErrorDialog(true);
      return;
    }

    uploadMutation.mutate(
      {
        files: validFiles,
        docType,
        userEmail: user.email,
        dbName,
      },
      {
        onSuccess: (result) => {
          if (result.failed.length > 0) {
            setUploadError(true);
            setFailedFiles(result.failed);
            setShowErrorDialog(true);
          } else {
            setSuccess(true);
            onUploadComplete?.();
          }
        },
        onError: () => {
          setUploadError(true);
          setFailedFiles(validFiles.map(f => f.name));
          setShowErrorDialog(true);
        },
      }
    );

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getButtonIcon = () => {
    if (uploadMutation.isPending) return <Loader2 className="h-4 w-4 animate-spin" />;
    if (success) return <Check className="h-4 w-4" />;
    if (uploadError) return <X className="h-4 w-4" />;
    return <Upload className="h-4 w-4" />;
  };

  const getButtonVariant = () => {
    if (success) return "default";
    if (uploadError) return "destructive";
    return "secondary";
  };

  return (
    <>
      <Card className={cn(
        "min-h-[140px]",
        span > 1 && `col-span-${span}`,
        className
      )}>
        <CardContent className="p-6 flex flex-col justify-between h-full">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {icon && (
                <div className="mb-2 text-foreground">
                  {icon}
                </div>
              )}
              <p className="text-sm font-medium text-muted-foreground mb-2">
                Arquivos {allowedExtensions}
              </p>
              <p className="text-lg font-semibold text-foreground">
                {title}
              </p>
            </div>
            <div className="ml-4 flex-shrink-0">
              <Button
                variant={getButtonVariant()}
                size="icon"
                className="h-12 w-12 rounded-full"
                onClick={handleButtonClick}
                disabled={uploadMutation.isPending}
              >
                {getButtonIcon()}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept={allowedExtensions}
                multiple
                className="hidden"
                onChange={fileUploadHandler}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Dialog */}
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {validationErrors.length > 0
                ? "Arquivos com problemas de segurança:"
                : "Upload falhou para os seguintes arquivos:"}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-60 overflow-y-auto">
            <ul className="space-y-2">
              {validationErrors.length > 0
                ? validationErrors.map((error, index) => (
                    <li key={index} className="text-sm text-destructive">
                      {error}
                    </li>
                  ))
                : failedFiles.map((fileName) => (
                    <li key={fileName} className="text-sm text-destructive">
                      {fileName}
                    </li>
                  ))
              }
            </ul>
          </div>
          <DialogFooter>
            <Button onClick={handleClose}>
              Sair
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}